import React, { createContext, useContext, useState, useCallback } from 'react';

const MenuSchedulingContext = createContext(null);

export const useMenuScheduling = () => {
    const context = useContext(MenuSchedulingContext);
    if (!context) {
        throw new Error('useMenuScheduling must be used within MenuSchedulingProvider');
    }
    return context;
};

export const MenuSchedulingProvider = ({ children }) => {
    const [selectedRecipeIds, setSelectedRecipeIds] = useState(new Set());
    const [selectedRecipes, setSelectedRecipes] = useState(new Map()); // Map<recipeId, recipeObject>
    const [recipeOrder, setRecipeOrder] = useState([]); // Array of recipeIds in order
    const [isSchedulingMode, setIsSchedulingMode] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [scheduledPlan, setScheduledPlan] = useState(null);
    const [currentView, setCurrentView] = useState('selection'); // 'selection' | 'results'

    const toggleRecipeSelection = useCallback((recipeId, recipe = null) => {
        setSelectedRecipeIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(recipeId)) {
                newSet.delete(recipeId);
            } else {
                newSet.add(recipeId);
            }
            return newSet;
        });
        
        // Also update the recipes map and order
        if (recipe) {
            setSelectedRecipes(prev => {
                const newMap = new Map(prev);
                if (newMap.has(recipeId)) {
                    newMap.delete(recipeId);
                } else {
                    newMap.set(recipeId, recipe);
                }
                return newMap;
            });
            
            setRecipeOrder(prev => {
                if (prev.includes(recipeId)) {
                    return prev.filter(id => id !== recipeId);
                } else {
                    return [...prev, recipeId];
                }
            });
        }
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedRecipeIds(new Set());
        setSelectedRecipes(new Map());
        setRecipeOrder([]);
        setScheduledPlan(null);
        setCurrentView('selection');
    }, []);

    const openScheduleModal = useCallback(() => {
        setIsSchedulingMode(true);
        setShowScheduleModal(true);
        setCurrentView('selection');
    }, []);

    const closeScheduleModal = useCallback(() => {
        setShowScheduleModal(false);
        setIsSchedulingMode(false);
        // Don't clear selection immediately - let animation finish
        setTimeout(() => {
            clearSelection();
        }, 300);
    }, [clearSelection]);

    const setSchedule = useCallback((plan) => {
        setScheduledPlan(plan);
        setCurrentView('results');
    }, []);

    const backToSelection = useCallback(() => {
        setScheduledPlan(null);
        setCurrentView('selection');
    }, []);
    
    // Helper to add recipe to map without toggling selection
    const addRecipeToMap = useCallback((recipeId, recipe) => {
        setSelectedRecipes(prev => {
            const newMap = new Map(prev);
            if (!newMap.has(recipeId)) {
                newMap.set(recipeId, recipe);
            }
            return newMap;
        });
    }, []);

    const updateRecipeOrder = useCallback((order) => {
        setRecipeOrder(order);
    }, []);

    const value = {
        selectedRecipeIds,
        selectedRecipes,
        recipeOrder,
        updateRecipeOrder,
        isSchedulingMode,
        showScheduleModal,
        scheduledPlan,
        currentView,
        toggleRecipeSelection,
        addRecipeToMap,
        clearSelection,
        openScheduleModal,
        closeScheduleModal,
        setSchedule,
        backToSelection,
    };

    return (
        <MenuSchedulingContext.Provider value={value}>
            {children}
        </MenuSchedulingContext.Provider>
    );
};

